import React, { useState, useEffect } from 'react';
import { Stack, Box, Typography } from '@mui/material';
import Widget from 'shared/components/Widget';
import { ICandidateProfileApplicationHistory } from 'modules/hiring/models/candidateProfile.model';
import CandidateEducationPage from './candidateEducationPage';
import openIcon from '../../../../assets/openIcon.svg';
import styles from './candidateApplicationProfileDetails.module.scss';
import { Canceler } from 'axios';
import {
  axiosGetDownloadAssessmentReportDetails,
  axiosGetDownloadDynamicReportDetails,
  axiosGetAssessmentStatus,
} from '../../stateManagement/api';
import {
  ICandidateAssessmentReportPayload,
  ICandidateAssessmentResponse,
  ICandidateDynamicReportResponse,
} from 'modules/hiring/models/candidateProfile.model';
import {
  HEIRING_MANAGER_REPORT,
  KF4D_INTERVIEW_GUIDE,
} from 'shared/stateManagement/constants';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'utilities/hooks/useConfig';
import NoDataBox from '../NoDataBox';
import LabelValue from '../LabelValue';
import CandidateResumeData from './CandidateResumeData';
import LabelValuePair from '../labelValuePair';

export interface ICandidateProfileApplicationDetails {
  data: ICandidateProfileApplicationHistory | undefined;
  contactData: any;
  candidateId: number;
  loading: boolean;
}

const CandidateApplicationProfileDetails: React.FC<
  ICandidateProfileApplicationDetails
> = ({ data, contactData, candidateId, loading }) => {
  let assessmentReports: Canceler;

  const [completed, setCompleted] = useState<boolean>(false);
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [notStarted, setNotStarted] = useState<boolean>(false);
  const [notInvited, setNotInvited] = useState<boolean>(false);
  const [isLoadingForStatus, setIsLoadingForStatus] = useState<boolean>(false);
  const [empty, setEmpty] = useState<boolean>(false);
  const [dynamicReportEnabled, setDynamicReportEnabled] =
    useState<boolean>(false);
  const [dynamicReportData, setDynamicReportData] = useState();
  const { config } = useConfig();
  const { t: translate } = useTranslation();

  function statusEmpty() {
    setEmpty(true);
    setNotInvited(false);
    setNotStarted(false);
    setInProgress(false);
    setCompleted(false);
  }

  useEffect(() => {
    //call api to get the assessment status
    const getAssessment = async () => {
      try {
        if (
          config?.kfasClientId &&
          config?.isKfasEnabled &&
          data?.participantId &&
          !data?.isParticipantDeleted
        ) {
          setIsLoadingForStatus(true);

          const responseStatus: any = await axiosGetAssessmentStatus(
            config?.kfasClientId,
            data?.participantId,
            data?.kfasProjectId
          );
          if (responseStatus?.statusCode === 200) {
            if (responseStatus?.assessmentStatus === 'NOT_STARTED') {
              setNotStarted(true);
              setNotInvited(false);
              setInProgress(false);
              setCompleted(false);
              setEmpty(false);
            } else if (responseStatus?.assessmentStatus === 'NOT_INVITED') {
              setNotStarted(false);
              setNotInvited(true);
              setInProgress(false);
              setCompleted(false);
              setEmpty(false);
            } else if (responseStatus?.assessmentStatus === 'IN_PROGRESS') {
              setNotStarted(false);
              setNotInvited(false);
              setInProgress(true);
              setCompleted(false);
              setEmpty(false);
            } else if (responseStatus?.assessmentStatus === 'COMPLETED') {
              setNotStarted(false);
              setNotInvited(false);
              setInProgress(false);
              setCompleted(true);
              setEmpty(false);
            } else {
              statusEmpty();
            }
          } else {
            statusEmpty();
          }

          setIsLoadingForStatus(false);
        }
      } catch (error) {
        throw new Error(`API Error: "${error}"`);
      }
    };

    getAssessment();
  }, [config, data]);

  useEffect(() => {
    if (data && data?.participantId) {
      getDownloadDynamicReport(
        data?.participantId,
        data?.kfasProjectSuccessProfileId,
        data?.kfasProjectId
      );
    }
  }, [data]);

  const getAssessmentReport = async (type: string) => {
    try {
      const payload: ICandidateAssessmentReportPayload = {
        participantId: data?.participantId!,
        projectId: data?.kfasProjectId!,
        successProfileId: data?.kfasProjectSuccessProfileId!,
        reportType: type,
        kfasClientId: config?.kfasClientId!,
      };
      const response: ICandidateAssessmentResponse =
        await axiosGetDownloadAssessmentReportDetails(
          payload,
          (cancel: Canceler) => (assessmentReports = cancel)
        );
      if (response?.status === 200) {
        const fileType = response?.headers['content-type'];
        const fileName =
          response?.headers['content-disposition'].split('filename=')[1];
        const blob = new Blob([response?.data], {
          type: fileType,
        });
        const url = window.URL.createObjectURL(blob);
        // window.open(url, '_blank');
        let alink = document.createElement('a');
        alink.href = url;
        alink.target = '_blank';
        alink.download = fileName;
        alink.click();
      }
    } catch (err: any) {
      throw new Error(`API Error: "${err}"`);
    } finally {
      //setIsLoading(false);
    }
  };

  const getDownloadDynamicReport = async (
    participantId: any,
    kfasProjectSuccessProfileId: any,
    kfasProjectId: any
  ) => {
    try {
      if (config?.kfasClientId) {
        const response: ICandidateDynamicReportResponse =
          await axiosGetDownloadDynamicReportDetails(
            participantId,
            kfasProjectSuccessProfileId,
            config?.kfasClientId,
            kfasProjectId
          );
        if (response?.statusCode === 200) {
          if (response?.reports.length > 0) {
            setDynamicReportData(response?.reports);
            setDynamicReportEnabled(true);
          } else {
            setDynamicReportEnabled(false);
          }
        }
      }
    } catch (err: any) {
      //throw new Error(`API Error: "${err}"`);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const getDownloadDynamicReports = async(dynamicReportData: any) => {
    if (dynamicReportData) {
      let updateUrl = dynamicReportData[0]?.reportUrl;
      if (updateUrl) {
        let updatedNewUrl = updateUrl.slice(1, -1);
        if (updatedNewUrl) {
          window.open(updatedNewUrl, '_blank');
          // Refresh dynamicReportData after opening the report
        if (data?.participantId) {
          await getDownloadDynamicReport(
            data?.participantId,
            data?.kfasProjectSuccessProfileId,
            data?.kfasProjectId
          );
        }
        }
      }
    }
  };

  return (
    <Stack id='candidate-profile-container' gap='1.25rem' mt='1.5rem'>
      <Stack
        id='candidate-profile-content-container'
        className={styles.candidateProfileContentContainer}
      >
        <Widget
          id='candidate-profile-contact-wrapper'
          className={styles.candidateProfileContactWrapper}
          sx={{
            borderRadius: ({ typography }) => typography.pxToRem(8),
            backgroundColor: '#F8F9FB',
          }}
          headingProps={{
            id: 'contact-heading',
            i18nKey: 'contact',
            variant: 'h3',
          }}
          withDivider
          dividerProps={{
            sx: {
              mb: ({ typography }) => typography.pxToRem(28),
            },
          }}
        >
          <Box className={styles.candidateDetails}>
            <CandidateResumeData
              candidateId={candidateId}
              firstName={data?.firstName}
              lastName={data?.lastName}
            />
            <LabelValuePair
              id='emailID'
              label='eMail'
              value={contactData?.email}
            />
            <LabelValue
              id='no-of-job-applications-in-progress'
              label='numberofApplicationsInProgress'
              value={contactData?.numberofApplicationsInProgress}
            />
            <LabelValuePair
              id='telephone'
              label='telephone'
              value={contactData?.phone}
            />
            <LabelValuePair
              id='location'
              label='location'
              value={contactData?.location}
            />
          </Box>
        </Widget>

        {config?.isKfasEnabled && (
          <Widget
            id='candidate-profile-assessment-wrapper'
            sx={{
              borderRadius: ({ typography }) => typography.pxToRem(8),
              backgroundColor: '#F8F9FB',
            }}
            headingProps={{
              id: 'assessment-report-heading',
              i18nKey: 'assessmentReport',
              variant: 'h3',
            }}
            withDivider
            dividerProps={{
              sx: {
                mb: ({ typography }) => typography.pxToRem(28),
              },
            }}
          >
            {completed ? (
              <>
                {' '}
                <Typography
                  className={`kfic2-bodyTextLink`}
                  onClick={() => getAssessmentReport(KF4D_INTERVIEW_GUIDE)}
                  sx={{ marginTop: '-10px' }}
                >
                  {translate('interviewGuid')}
                  <img
                    src={openIcon}
                    title='Interview Guid'
                    alt='Interview Guid Icon'
                    data-testid='thumbnail'
                    style={{
                      marginTop: 3,
                      position: 'relative',
                      paddingLeft: 8,
                    }}
                  />
                </Typography>
                {dynamicReportEnabled && (
                  <Typography
                    className={`kfic2-bodyTextLink`}
                    onClick={() => getDownloadDynamicReports(dynamicReportData)}
                    sx={{ marginTop: '10px' }}
                  >
                    {translate('dynamicFeedback')}
                    <img
                      src={openIcon}
                      title='Dynamic Feedback'
                      alt='Dynamic Feedback Icon'
                      data-testid='thumbnail'
                      style={{
                        marginTop: 3,
                        position: 'relative',
                        paddingLeft: 8,
                      }}
                    />
                  </Typography>
                )}
                <Typography
                  className={`kfic2-bodyTextLink`}
                  onClick={() => getAssessmentReport(HEIRING_MANAGER_REPORT)}
                  sx={{ marginTop: '10px' }}
                >
                  {translate('hiringManager')}
                  <img
                    src={openIcon}
                    title='Hiring Manager'
                    alt='Hiring Manager Icon'
                    data-testid='thumbnail'
                    style={{
                      marginTop: 3,
                      position: 'relative',
                      paddingLeft: 8,
                    }}
                  />
                </Typography>
              </>
            ) : inProgress ? (
              <NoDataBox
                text={translate('noAssessmentReportToDisplayInProgress')}
              />
            ) : (
              <NoDataBox
                text={translate('noAssessmentReportToDisplayNotStarted')}
              />
            )}
          </Widget>
        )}
      </Stack>

      <Widget
        id='candidate-profile-history-wrapper'
        sx={{
          borderRadius: ({ typography }) => typography.pxToRem(8),
          backgroundColor: '#F8F9FB',
        }}
        dividerProps={{
          sx: {
            mb: ({ typography }) => typography.pxToRem(28),
          },
        }}
        style={{
          width: '100%',
        }}
      >
        <CandidateEducationPage candidateId={candidateId} />
      </Widget>
    </Stack>
  );
};

export default CandidateApplicationProfileDetails;
